import {
  App,
  Button, Col, Flex, Form, Modal, Row, Space, Typography, Input
} from "antd";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Scheduler from "../Scheduler";
import { MODIFY_APPOINTMENT, DELETE_APPOINTMENT } from "../../queries/appointments.gql";
import { useAppDispatch } from "../../store";
import { fetchAppointments } from "../../store/slices/user";
import { fetchSchedulesData } from "../../store/slices/scheduler";
import useCurrentUser from "../../hooks/useCurrentUser";

const { Title, Text } = Typography;
const { TextArea } = Input;

const AppointmentModal = ({ appointmentId, isOpen, onClose }: {
  appointmentId: number,
  isOpen: boolean,
  onClose: () => void
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [selectedData, setSelectedData] = useState("");
  const dispatch = useAppDispatch();
  const { refetchUser } = useCurrentUser();
  const [modalOpen, setModalOpen] = useState(false);

  const onModalClose = () => {
    onClose();
    setSelectedData("");
    form.resetFields();
  };

  const [modifyAppointment, { loading }] = useMutation(MODIFY_APPOINTMENT, {
    fetchPolicy: "no-cache",

    onCompleted(data) {
      if (data.modifyAppointment.id) {
        dispatch(fetchAppointments());
        dispatch(fetchSchedulesData({}));
        onModalClose();
        message.success(t("apollo.success.modifyAppointment"));
      }
    },
    onError(error) {
      message.error(error.graphQLErrors[0].message);
    }
  });

  const [deleteAppointment,
    { loading: deleteAppointmentLoading }] = useMutation(DELETE_APPOINTMENT, {
    fetchPolicy: "no-cache",

    onCompleted(data) {
      if (data.deleteAppointment) {
        refetchUser();
        dispatch(fetchAppointments());
        dispatch(fetchSchedulesData({}));

        onModalClose();
        message.success(t("apollo.success.deleteAppointment"));
      }
    },
    onError(error) {
      message.error(error.graphQLErrors[0].message);
    }
  });

  const cancelAppointment = async () => {
    await deleteAppointment({
      variables: {
        id: appointmentId,
        motivation: form.getFieldValue("motivation") ?? "Nessuna motivazione"
      }
    });
  };

  return (
    <Modal
      onCancel={onModalClose}
      width={1143}
      destroyOnClose
      centered
      open={isOpen}
      footer={null}
    >
      <Row gutter={[20, 20]}>
        <Col span={24} lg={12}>
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <Title level={3} style={{ marginBottom: 0 }}>
              {t("appointment.modify.title")}
            </Title>
            <Text type="secondary" style={{ lineHeight: 1, fontSize: 20, marginBottom: 20 }}>
              {t("appointment.modify.textp1")}<br />
              {t("appointment.modify.textp2")}<strong>&quot;{t("appointment.modify.submitButton")}&quot;</strong>.<br />
              {t("appointment.modify.textp3")}<strong>&quot;{t("appointment.delete.submitButton")}&quot;</strong>.
            </Text>
            <Form
              name="modify-appointment"
              layout="vertical"
              requiredMark="optional"
              form={form}
              onFinishFailed={() => message.error(t("apollo.error.modifyAppointment"))}
              autoComplete="off"
              onFinish={(values) => {
                modifyAppointment({
                  variables: {
                    id: appointmentId,
                    startAt: selectedData,
                    motivation: values.motivation ?? "Nessuna motivazione"
                  }
                });
              }}
            >
              <Form.Item
                label={t("form.label.motivation")}
                name="motivation"
                style={{ marginBottom: 0 }}
              >
                <TextArea rows={4} />
              </Form.Item>
            </Form>
            <Flex gap={16}>
              <Button
                type="primary"
                size="large"
                disabled={!selectedData.length || loading}
                loading={loading}
                onClick={() => form.submit()}
                block
              >
                {t("appointment.modify.submitButton")}
              </Button>
              <Button
                size="large"
                disabled={deleteAppointmentLoading}
                loading={deleteAppointmentLoading}
                danger
                block
                onClick={() => setModalOpen(true)}
              >
                {t("appointment.delete.submitButton")}
              </Button>
            </Flex>
          </Space>
        </Col>
        <Col span={24} lg={12}>
          <Flex justify="center">
            <Title level={3} style={{ marginBottom: 20 }}>{t("appointment.modify.subtitle")}</Title>
          </Flex>
          <Scheduler onTimeClick={(schedule) => setSelectedData(`${schedule.current} ${schedule.from}:00`)} defaultDays={3} size="small" />
        </Col>
      </Row>
      <Modal
        title={`${t("appointment.delete.confirmation")}?`}
        open={modalOpen}
        onOk={cancelAppointment}
        onCancel={() => setModalOpen(false)}
        okText={t("global.button.yes")}
        cancelText={t("global.button.cancel")}
      />
    </Modal>
  );
};

export default AppointmentModal;
